/**
 * Tipos do catálogo servido pelos endpoints públicos do card-inventory-server:
 *   GET /api/cards/catalog        -> CardCatalog
 *   GET /api/territories/catalog  -> TerritoryCatalog
 *
 * Estes tipos espelham `@card-game/protocol` (cards/CardMetadata.ts,
 * game/TerritoryMetadata.ts, actions/TargetSpec.ts, cards/CreatureKind.ts,
 * cards/Gender.ts). Mantemos uma cópia local para que o guia seja um projeto
 * independente, sem depender da build do pacote de protocolo.
 */

/** Os três tipos de carta do jogo. */
export type CardType = 'unit' | 'spell' | 'trap';

/** Tags de facção/arquétipo que uma unidade pode carregar (pode ter várias ou nenhuma). */
export type CreatureKind =
  | 'gado'
  | 'felina'
  | 'autoridade'
  | 'politico'
  | 'intergalactico'
  | 'esquerdopata'
  | 'crime'
  | 'bostileiros'
  | 'artista';

/** Lista canônica das tags, na ordem de exibição. */
export const CREATURE_KINDS: readonly CreatureKind[] = [
  'gado',
  'felina',
  'autoridade',
  'politico',
  'intergalactico',
  'esquerdopata',
  'crime',
  'bostileiros',
  'artista',
];

/** Gênero que a unidade apresenta (opcional). */
export type Gender = 'male' | 'female' | 'fluid';

/** Restringe quais unidades um feitiço pode mirar em relação ao conjurador. */
export type UnitFilter = 'anyUnit' | 'enemyUnit' | 'allyUnit';

/** Descreve os requisitos de alvo de uma carta de feitiço. */
export type TargetSpec =
  | { kind: 'none' }
  | { kind: 'singleUnit'; filter: UnitFilter }
  | { kind: 'territory' }
  | { kind: 'multipleUnits'; count: number; filter: UnitFilter };

/** Campos comuns a toda carta. */
export interface BaseCardMetadata {
  cardId: string;
  name: string;
  cost: number;
  type: CardType;
  description: string;
  artUrl: string;
  vfxHint?: string;
  soundHint?: string;
}

export interface UnitCardMetadata extends BaseCardMetadata {
  type: 'unit';
  baseAttack: number;
  kinds: CreatureKind[];
  gender?: Gender;
}

export interface SpellCardMetadata extends BaseCardMetadata {
  type: 'spell';
  targetSpec: TargetSpec;
}

export interface TrapCardMetadata extends BaseCardMetadata {
  type: 'trap';
}

/** União discriminada de todas as variantes de carta, pela chave `type`. */
export type CardMetadata = UnitCardMetadata | SpellCardMetadata | TrapCardMetadata;

export interface CardCatalog {
  version: string;
  cards: CardMetadata[];
}

/** Força/impacto relativo da regra de um território, usada para apresentação. */
export type TerritoryCategory = 'light' | 'medium' | 'heavy';

export interface TerritoryRuleMetadata {
  ruleId: string;
  name: string;
  description: string;
  artUrl: string;
  category: TerritoryCategory;
}

export interface TerritoryCatalog {
  version: string;
  rules: TerritoryRuleMetadata[];
}

/** Narrowing helper para o tipo de carta unidade. */
export function isUnit(card: CardMetadata): card is UnitCardMetadata {
  return card.type === 'unit';
}
